import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const GLOBAL_IMG =
  "https://images.unsplash.com/photo-1712431777300-f7d37c0a4d82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function WhoWeAre() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section
      id="approach"
      ref={ref}
      data-testid="approach-section"
      className="relative bg-emerald-900 text-base-bg py-24 md:py-36 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* Chapter number */}
          <div className="lg:col-span-3">
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
              <span className="font-serif text-7xl md:text-8xl text-base-bg/25 leading-none">01</span>
              <p className="mt-4 text-[0.72rem] uppercase tracking-[0.22em] text-base-bg/50">Who we are</p>
            </motion.div>
          </div>

          {/* Content indented */}
          <div className="lg:col-span-6 lg:pr-8">
            <motion.h2
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight"
            >
              We don't just invest capital. We invest in <span className="italic text-sand">perspective.</span>
            </motion.h2>

            <div className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-base-bg/75 max-w-xl">
              <motion.p custom={1} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
                At TILT, we look for the angles others miss. We are agnostic to geography but loyal
                to our heritage. Whether it's a disruptive tech startup in Europe or a traditional
                manufacturing opportunity further afield, our criterion is simple: is it interesting?
                And can it deliver a good return?
              </motion.p>
              <motion.p custom={2} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
                We are private by nature, which allows us to be patient, decisive, and free from the
                noise of the public markets.
              </motion.p>
            </div>
          </div>

          {/* Clipped image */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden aspect-[3/4] border border-base-bg/20"
            >
              <motion.img
                src={GLOBAL_IMG}
                alt="Atmospheric Irish coastline representing reach beyond borders"
                style={{ y: imgY, scale: 1.15 }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
